import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ComplexOrderItem } from "../../common/types/interfaces";

interface FieldValue {
  fieldId: number;
  value: string;
}

export interface ComplexOrderCartState {
  items: ComplexOrderItem[];
  isOpen: boolean;
  total: number;
  phone: string;
  comment: string;
  fieldValues: FieldValue[];
}

const initialState: ComplexOrderCartState = {
  items: [],
  isOpen: false,
  total: 0,
  phone: "",
  comment: "",
  fieldValues: [],
};

const calculateTotal = (items: ComplexOrderItem[]): number =>
  items.reduce((sum, item) => sum + item.total, 0);

const complexOrderCartSlice = createSlice({
  name: "complexOrderCart",
  initialState,
  reducers: {
    addToComplexOrderCart: (
      state,
      action: PayloadAction<
        Omit<ComplexOrderItem, "quantity" | "id" | "total"> & {
          quantity?: number;
          parentId?: string;
        }
      >,
    ) => {
      const existingItem = state.items.find(
        (item) =>
          item.itemId === action.payload.itemId &&
          item.clusterId === action.payload.clusterId &&
          item.parentId === action.payload.parentId,
      );

      if (existingItem) {
        const step = action.payload.quantityStep || 1;
        existingItem.quantity += step;
        existingItem.total = existingItem.price * existingItem.quantity;
      } else {
        const quantity =
          action.payload.quantity || action.payload.minQuantity || 1;
        state.items.push({
          ...action.payload,
          id: `${action.payload.clusterId}_${action.payload.itemId}`,
          quantity,
          total: action.payload.price * quantity,
          parentId: action.payload.parentId || undefined,
          unit: action.payload.unit,
        });
      }
      state.total = calculateTotal(state.items);
    },

    removeFromComplexOrderCart: (state, action: PayloadAction<string>) => {
      const itemToRemove = state.items.find((i) => i.id === action.payload);
      if (!itemToRemove) return;

      // Удаляем сам элемент
      state.items = state.items.filter((i) => i.id !== action.payload);

      // Если удалили родителя — удаляем и всех его детей
      if (!itemToRemove.parentId) {
        state.items = state.items.filter((i) => i.parentId !== action.payload);
      } else {
        // Если удалили ребенка — проверяем, остались ли у родителя другие дети
        const siblings = state.items.filter((i) => i.parentId === itemToRemove.parentId);
        if (siblings.length === 0) {
          // Если детей больше нет — удаляем и родителя
          state.items = state.items.filter((i) => i.id !== itemToRemove.parentId);
        }
      }

      state.total = calculateTotal(state.items);
    },

    removeAllByParentId: (state, action: PayloadAction<string>) => {
      // Удаляем и родителя, и всех детей
      state.items = state.items.filter(
        (i) => i.id !== action.payload && i.parentId !== action.payload
      );
      state.total = calculateTotal(state.items);
    },

    increaseComplexOrderCartQuantity: (
      state,
      action: PayloadAction<string>,
    ) => {
      const item = state.items.find((item) => item.id === action.payload);
      if (item) {
        const step = item.quantityStep || 1;
        item.quantity += step;
        item.total = item.price * item.quantity;
        state.total = calculateTotal(state.items);
      }
    },

    decreaseComplexOrderCartQuantity: (
      state,
      action: PayloadAction<string>,
    ) => {
      const item = state.items.find((item) => item.id === action.payload);
      if (item) {
        const step = item.quantityStep || 1;
        const newQuantity = item.quantity - step;
        const minQuantity = item.minQuantity || 1;

        if (newQuantity < minQuantity) {
          // Безопасное удаление: убираем конкретный item
          const itemToRemove = state.items.find((i) => i.id === action.payload);
          if (itemToRemove) {
            state.items = state.items.filter((i) => i.id !== action.payload);
            if (itemToRemove.parentId) {
              const siblings = state.items.filter((i) => i.parentId === itemToRemove.parentId);
              if (siblings.length === 0) {
                state.items = state.items.filter((i) => i.id !== itemToRemove.parentId);
              }
            }
          }
        } else {
          item.quantity = newQuantity;
          item.total = item.price * item.quantity;
        }
        state.total = calculateTotal(state.items);
      }
    },

    clearComplexOrderCart: (state) => {
      state.items = [];
      state.total = 0;
      state.phone = "";
      state.comment = "";
      state.fieldValues = [];
    },

    setComplexOrderCartItems: (
      state,
      action: PayloadAction<ComplexOrderItem[]>,
    ) => {
      state.items = action.payload;
      state.total = calculateTotal(action.payload);
    },

    setComplexCartPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },

    setComplexCartComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload;
    },

    setComplexCartFieldValues: (state, action: PayloadAction<FieldValue[]>) => {
      state.fieldValues = action.payload;
    },

    openComplexOrderCartModal: (state) => {
      state.isOpen = true;
    },

    closeComplexOrderCartModal: (state) => {
      state.isOpen = false;
    },

    toggleComplexOrderCartModal: (state) => {
      state.isOpen = !state.isOpen;
    },
  },
});

export const {
  addToComplexOrderCart,
  removeFromComplexOrderCart,
  removeAllByParentId,
  increaseComplexOrderCartQuantity,
  decreaseComplexOrderCartQuantity,
  clearComplexOrderCart,
  setComplexOrderCartItems,
  setComplexCartPhone,
  setComplexCartComment,
  setComplexCartFieldValues,
  openComplexOrderCartModal,
  closeComplexOrderCartModal,
  toggleComplexOrderCartModal,
} = complexOrderCartSlice.actions;

export default complexOrderCartSlice.reducer;